import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import styled from "styled-components";
import { WebRtcPlayer } from "./WebRtcPlayer";
import { ClosedCaptions } from "./ClosedCaptions";
import { ListeningAnimation, ListeningMode } from "./ListeningAnimation";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Confetti from "react-confetti";
import {
  ButtonType,
  CantPlay,
  nextSpeakingRate,
  SpeakingRate,
  PlaybackState,
} from "../../common/api";
import {
  faMicrophone,
  faPlay,
  faCircleNotch,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { JwtContext } from "../../common/context/JwtContext";
import { ClientConfigContext } from "../../common/context/ClientConfigContext";
import { ApiContext } from "../../common/context/ApiContext";
import { NavContext } from "../../common/context/NavContext";
import { Account, AccountContext } from "../../common/context/AccountContext";
import { stringOrNullToNumberOrNull } from "../../common/utils";
import { TEMPORARY_SPEAKING_RATE_KEY } from "../../common/context/PreferencesProvider";
import {
  AnalyticsContext,
  ChangeLessonSpeedTapEvent,
  PauseLessonButtonTapEvent,
  ReadyToContinueButtonTapEvent,
  ResumeLessonButtonTapEvent,
} from "../../common/context/AnalyticsContext";
import { COLOR_GREEN, COLOR_WHITE, COLOR_BLUE } from "../../common/colors";

export const enum PlaybackActionsPlayPauseState {
  Play = 1,
  Pause = 2,
  PauseDisabled = 3,
}

export const enum ClosedCaptionsState {
  Disabled = 0,
  Enabled = 1,
}

export type PlaybackActions =
  | {
      pauseState: PlaybackActionsPlayPauseState;
      clickPlay: () => void;
      clickPause: () => void;
      speed: SpeakingRate;
      toggleSpeed: () => void;
      // Only present when the user supports closed captions; when absent,
      // the closed-captions button is hidden entirely.
      closedCaptionsState?: ClosedCaptionsState;
      toggleClosedCaptions?: () => void;
    }
  | undefined;

const ListeningWrapper = styled.div`
  width: calc(100%);
  overflow: hidden;
  display: flex;
  justify-content: center;
  z-index: 10;
`;

// Keeps the WebRTC video in a fixed aspect-ratio container, whether or not
// the video is hidden. This keeps the image preview positioned properly.
// The max-height cap is lowered when closed captions are shown so the video
// shrinks to make room for the caption box below it.
const WebRtcSizer = styled.div<{ $captionsEnabled?: boolean }>`
  max-height: ${({ $captionsEnabled }) => ($captionsEnabled ? "65vh" : "75vh")};
  aspect-ratio: 16 / 9;
  margin: 0 auto;
`;

const PreviewControlsDiv = styled.div`
  height: 40px;
  text-align: center;
  margin: 0px;
  margin-top: 5px;
`;

const ReadyToContinueButton = styled.button`
  background-color: ${COLOR_GREEN};
  border: none;
  border-radius: 13px;
  margin-right: 4px;
  cursor: pointer;
  height: 26px;
  width: 100px;
  color: ${COLOR_WHITE};
`;

export const enum PlayerMode {
  STOPPED = 1,
  PLAYING = 2,
  PAUSED = 3,
  CONNECTING = 4,
}

interface Props {
  account: Account;
  onLessonFinished: (
    showPostLessonScreen: boolean,
    cantPlay: CantPlay | null,
  ) => void;
  unitId: number | undefined;
  lessonId: number;
  version: number;
  temporaryFirstName?: string;
  isGuest: boolean;
  isLessonReady: boolean;
  ignoreState: boolean;
  customFlashpackId: number | null;
  setPlaybackActions: (playbackActions: PlaybackActions) => void;
}

/**
 * React component that controls preview playback of a lesson or any part of it.
 */
export const WebRtcPlayerWrapper = (props: Props) => {
  const { t } = useTranslation();
  const webrtcPlayer = React.useRef<WebRtcPlayer | null>(null);

  // This can be updated inside of WebRtcPlayer
  const playbackState = React.useRef<PlaybackState>(new PlaybackState());

  const [listeningMode, setListeningMode] = React.useState<ListeningMode>(
    ListeningMode.IDLE,
  );
  const [playerMode, setPlayerMode] = React.useState(PlayerMode.STOPPED);
  const [canPause, setCanPause] = React.useState(true);
  const [closedCaptionsEnabled, setClosedCaptionsEnabled] = React.useState(
    props.account.profile!.closed_captions_enabled,
  );
  const captionRestartPending = React.useRef(false);
  const [caption, setCaption] = React.useState<{
    text: string;
    progress: number;
  }>({ text: "", progress: 0 });
  const [showConfetti, setShowConfetti] = React.useState(false);
  const confettiTimeoutId = React.useRef<number | null>(null);
  const showConfettiTill = React.useRef<number>(0);
  const [mediaCaptureWarningOpen, setMediaCaptureWarningOpen] =
    React.useState(false);
  const [mediaCaptureWarningTitle, setMediaCaptureWarningTitle] =
    React.useState("");
  const [mediaCaptureWarningText, setMediaCaptureWarningText] =
    React.useState("");
  const [showReadyToContinueButton, setShowReadyToContinueButton] =
    React.useState(false);
  const [readyToContinueButtonType, setReadyToContinueButtonType] =
    React.useState(ButtonType.MICROPHONE);
  const { getJwt } = React.useContext(JwtContext);
  const { webSocketUrl } = React.useContext(ClientConfigContext);
  const { deleteLessonState, modifyProfile } = React.useContext(ApiContext);
  const { setNavbarSpecialActions } = React.useContext(NavContext);
  const { updateProfile } = React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);

  const speakingRate = React.useRef<SpeakingRate>(
    props.account.user.has_given_verified_consent &&
      !props.account.profile!.is_guest
      ? props.account.profile!.speaking_rate
      : (stringOrNullToNumberOrNull(
          localStorage.getItem(TEMPORARY_SPEAKING_RATE_KEY),
        ) ?? SpeakingRate.NORMAL),
  );

  React.useEffect(() => {
    if (showConfetti) {
      confettiTimeoutId.current = window.setTimeout(() => {
        if (showConfettiTill.current <= Date.now()) {
          confettiTimeoutId.current = null;
          setShowConfetti(false);
          showConfettiTill.current = 0;
        }
      }, showConfettiTill.current - Date.now());
    }
    return () => {
      if (confettiTimeoutId.current !== null) {
        clearTimeout(confettiTimeoutId.current);
      }
      confettiTimeoutId.current = null;
    };
  }, [showConfetti]);

  const startPlaying = React.useCallback(() => {
    webrtcPlayer.current?.playLesson(
      props.lessonId,
      props.customFlashpackId,
      props.unitId,
      props.version,
      "",
      false, // playSingleNode
      props.ignoreState && playerMode === PlayerMode.STOPPED, // ignoreState only if we were stopped - if paused or in flex time, we need to resume.
      !playbackState.current.sentStartPlaying, // askToResume
      props.temporaryFirstName,
    );
    setPlayerMode(PlayerMode.CONNECTING);
  }, [
    props.lessonId,
    props.customFlashpackId,
    props.unitId,
    props.version,
    props.temporaryFirstName,
    props.ignoreState,
    playerMode,
  ]);

  React.useEffect(() => {
    setNavbarSpecialActions({
      playbackActions: true,
    });
    return () => {
      setNavbarSpecialActions(undefined);
    };
  }, [setNavbarSpecialActions]);

  const pausePlaying = React.useCallback(() => {
    webrtcPlayer.current?.stopCapturingMedia();
    webrtcPlayer.current?.disconnect();
    setListeningMode(ListeningMode.IDLE);
    setShowReadyToContinueButton(false);
    setPlayerMode(PlayerMode.PAUSED);
    setCaption({ text: "", progress: 0 });
  }, []);

  const account = props.account;
  const setPlaybackActions = props.setPlaybackActions;

  // Autoplay the lesson when the component is mounted.
  React.useEffect(() => {
    if (playerMode === PlayerMode.STOPPED && props.isLessonReady) {
      startPlaying();
    }

    function setActions() {
      setPlaybackActions({
        speed: speakingRate.current,
        ...(account.user.supports_closed_captions
          ? {
              closedCaptionsState: closedCaptionsEnabled
                ? ClosedCaptionsState.Enabled
                : ClosedCaptionsState.Disabled,
              toggleClosedCaptions: () => {
                const newValue = !closedCaptionsEnabled;
                captionRestartPending.current = true;
                setClosedCaptionsEnabled(newValue);
                if (
                  account.user.has_given_verified_consent &&
                  !account.profile!.is_guest
                ) {
                  modifyProfile(
                    account.profile!.id,
                    account.profile!.first_name,
                    account.profile!.first_name_phonetics,
                    speakingRate.current,
                    account.profile!.asr_english_accent,
                    account.profile!.teacher_avatar_info.id,
                    newValue,
                    true,
                  ).then((profile) => {
                    if (profile !== undefined) {
                      updateProfile(profile);
                    }
                  });
                }
              },
            }
          : {}),
        pauseState:
          playerMode === PlayerMode.PAUSED
            ? PlaybackActionsPlayPauseState.Play
            : canPause
              ? PlaybackActionsPlayPauseState.Pause
              : PlaybackActionsPlayPauseState.PauseDisabled,
        clickPlay: () => {
          logEvent(new ResumeLessonButtonTapEvent());
          startPlaying();
        },
        clickPause: () => {
          logEvent(new PauseLessonButtonTapEvent());
          pausePlaying();
        },
        toggleSpeed: () => {
          const newRate = nextSpeakingRate(speakingRate.current);
          speakingRate.current = newRate;
          logEvent(
            new ChangeLessonSpeedTapEvent(
              newRate === SpeakingRate.FAST
                ? "Fast"
                : newRate === SpeakingRate.NORMAL
                  ? "Normal"
                  : "Slow",
            ),
          );
          setActions();
          webrtcPlayer.current?.setSpeakingRate(newRate);
          if (
            account.user.has_given_verified_consent &&
            !account.profile!.is_guest
          ) {
            modifyProfile(
              account.profile!.id,
              account.profile!.first_name,
              account.profile!.first_name_phonetics,
              speakingRate.current,
              account.profile!.asr_english_accent,
              account.profile!.teacher_avatar_info.id,
              account.profile!.closed_captions_enabled,
              true,
            ).then((profile) => {
              if (profile !== undefined) {
                updateProfile(profile);
              }
            });
          } else {
            localStorage.setItem(
              TEMPORARY_SPEAKING_RATE_KEY,
              speakingRate.current.toString(),
            );
          }
        },
      });
    }
    setActions();

    return () => {
      setPlaybackActions(undefined);
    };
  }, [
    canPause,
    closedCaptionsEnabled,
    modifyProfile,
    pausePlaying,
    playerMode,
    startPlaying,
    updateProfile,
    account,
    props.isLessonReady,
    setPlaybackActions,
    logEvent,
  ]);

  // Handle closed captions being toggled mid-lesson.
  React.useEffect(() => {
    if (!captionRestartPending.current) {
      return;
    }
    captionRestartPending.current = false;
    if (playerMode === PlayerMode.PLAYING) {
      pausePlaying();
      startPlaying();
    }
  }, [closedCaptionsEnabled, playerMode, pausePlaying, startPlaying]);

  const onLessonStopped = (
    showPostLessonScreen: boolean,
    cantPlay: CantPlay | null,
  ) => {
    setShowReadyToContinueButton(false);
    setCaption({ text: "", progress: 0 });
    props.onLessonFinished(showPostLessonScreen, cantPlay);
  };

  const onCaptionFull = (text: string) => {
    setCaption({ text, progress: 0 });
  };

  const onCaptionProgress = (text: string) => {
    setCaption((current) => ({ ...current, progress: text.length }));
  };

  const onShowListeningUI = (stream: MediaStream) => {
    setListeningMode(ListeningMode.WAITING_FOR_VOICE_ACTIVITY);
  };

  const onVoiceActivity = (detected: boolean) => {
    if (listeningMode !== ListeningMode.IDLE) {
      setListeningMode(
        detected ? ListeningMode.SPEAKING : ListeningMode.NOT_SPEAKING,
      );
    }
  };

  const onHideListeningUI = () => {
    setListeningMode(ListeningMode.IDLE);
  };

  const onAnswerCaptureStarted = () => {
    setShowReadyToContinueButton(false);
  };

  const onAnswerCaptureDidNotStart = () => {
    // Not exactly same logic as in "stopPlaying", so please
    // don't just call that method from here!!
    setMediaCaptureWarningTitle(t("answer_capture_did_not_start_title"));
    setMediaCaptureWarningText(t("answer_capture_did_not_start_body"));
    setMediaCaptureWarningOpen(true);
    setPlayerMode(PlayerMode.PAUSED);
    setShowReadyToContinueButton(false);
    webrtcPlayer.current?.disconnect();
  };

  const onAudioVideoCaptureDidNotStart = () => {
    setMediaCaptureWarningTitle(t("audio_video_capture_did_not_start_title"));
    setMediaCaptureWarningText(t("audio_video_capture_did_not_start_body"));
    setMediaCaptureWarningOpen(true);
    // Lesson did not start, so we don't have to stop anything.
  };

  const onAnswerCaptureStopped = () => {};

  const onPlayConfetti = () => {
    showConfettiTill.current = Date.now() + 5000;
    setShowConfetti(true);
    confettiTimeoutId.current = window.setTimeout(() => {
      if (showConfettiTill.current <= Date.now()) {
        confettiTimeoutId.current = null;
        setShowConfetti(false);
        showConfettiTill.current = 0;
      }
    }, 5000);
  };

  const onWaitTime = (time: number, buttonType: ButtonType) => {
    setShowReadyToContinueButton(true);
    setReadyToContinueButtonType(buttonType);
  };

  const isConnecting =
    playerMode === PlayerMode.STOPPED || playerMode === PlayerMode.CONNECTING;

  const readyToContinue = () => {
    setShowReadyToContinueButton(false);
    setReadyToContinueButtonType(ButtonType.MICROPHONE);
    if (webrtcPlayer.current?.shouldBeConnected) {
      webrtcPlayer.current?.readyToContinue();
    } else {
      startPlaying();
    }
  };

  return (
    <>
      <div
        style={{
          flex: "1 1 100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            maxHeight: "80vh",
            paddingTop: "10px",
          }}
        >
          {showConfetti && <Confetti />}
          <WebRtcSizer $captionsEnabled={closedCaptionsEnabled}>
            {isConnecting && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  margin: 0,
                  height: "100%",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div>
                    <FontAwesomeIcon
                      icon={faCircleNotch}
                      spin={true}
                      size="4x"
                    />
                  </div>
                  <div>Connecting...</div>
                </div>
              </div>
            )}
            <WebRtcPlayer
              ref={webrtcPlayer}
              t={t}
              profileId={props.account.profile!.id}
              shouldHideVideo={isConnecting}
              lessonPlayer={{
                onLessonPlayingStateUpdated: (isPlaying) => {
                  if (isPlaying) {
                    setPlayerMode(
                      isPlaying ? PlayerMode.PLAYING : PlayerMode.STOPPED,
                    );
                  }
                },
                onCanPauseUpdated: setCanPause,
                onShowListeningUI: onShowListeningUI,
                onHideListeningUI: onHideListeningUI,
                onVoiceActivity: onVoiceActivity,
                onAnswerCaptureStarted: onAnswerCaptureStarted,
                onAnswerCaptureDidNotStart: onAnswerCaptureDidNotStart,
                onAudioVideoCaptureDidNotStart: onAudioVideoCaptureDidNotStart,
                onAnswerCaptureStopped: onAnswerCaptureStopped,
                onLessonStopped: onLessonStopped,
                onPlayConfetti: onPlayConfetti,
                onWaitTime: onWaitTime,
                onCaptionFull: onCaptionFull,
                onCaptionProgress: onCaptionProgress,
              }}
              speakingRate={speakingRate.current}
              supportsClosedCaptions={props.account.user.supports_closed_captions}
              closedCaptionsEnabled={closedCaptionsEnabled}
              playbackState={playbackState}
              deleteLessonState={() => {
                if (props.isGuest) {
                  deleteLessonState(
                    props.lessonId,
                    props.version,
                    props.account.profile!.id,
                  );
                }
              }}
              getJwt={getJwt}
              webSocketUrl={webSocketUrl}
            />
          </WebRtcSizer>
        </div>
        {closedCaptionsEnabled && caption.text.trim() !== "" && (
          <ClosedCaptions
            fullText={caption.text}
            progressChars={caption.progress}
          />
        )}
        <PreviewControlsDiv>
          {showReadyToContinueButton ? (
            <div style={{ textAlign: "center" }}>
              <ReadyToContinueButton
                onClick={() => {
                  logEvent(new ReadyToContinueButtonTapEvent());
                  readyToContinue();
                }}
                style={{
                  backgroundColor:
                    readyToContinueButtonType === ButtonType.MICROPHONE
                      ? COLOR_GREEN
                      : COLOR_BLUE,
                }}
              >
                {readyToContinueButtonType === ButtonType.MICROPHONE ? (
                  <FontAwesomeIcon
                    style={{ position: "relative" }}
                    icon={faMicrophone}
                    size="1x"
                  />
                ) : (
                  <FontAwesomeIcon
                    style={{ position: "relative" }}
                    icon={faPlay}
                    size="1x"
                  />
                )}
              </ReadyToContinueButton>
            </div>
          ) : (
            <ListeningWrapper>
              <ListeningAnimation mode={listeningMode} />
            </ListeningWrapper>
          )}
        </PreviewControlsDiv>
      </div>
      <div>
        <Dialog
          open={mediaCaptureWarningOpen}
          onClose={() => setMediaCaptureWarningOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {mediaCaptureWarningTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {mediaCaptureWarningText}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMediaCaptureWarningOpen(false)} autoFocus>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};
